import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { AddTagGroupComponent } from 'app/home/add-tag-group.component';
import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { AppUtilService } from 'app/shared/service/app-util.service';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { MetaDataService } from 'app/shared/service/meta-data.service';
import { TeamService } from 'app/shared/service/team.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';

@Component({
  selector: 'cc-team-tag-groups',
  templateUrl: 'team-tag-groups.component.html',
  styleUrls: ['home-settings.component.scss'],
})
export class TeamTagGroupsComponent implements OnInit, OnDestroy {
  unSubscribe = new Subject<void>();
  teamId;
  projectId;
  tagGroups;
  projectTagGroups;
  isWorking;
  teamTagTitle;
  projectTagTitle;
  readonly = false;

  constructor(
    private readonly appUtilService: AppUtilService,
    private readonly metadataService: MetaDataService,
    private readonly toastr: ToastrService,
    private readonly errorHandler: ErrorHandler,
    private readonly teamService: TeamService,
    private readonly metaDataService: MetaDataService,
    private readonly modal: NgbModal,
    private readonly confirmService: ConfirmService,
    private readonly i18nService: TmpI18NService,
    private readonly userService: UserService,
  ) {
    this.teamTagTitle = this.i18nService.getMessage('teamTagTitle');
    this.projectTagTitle = this.i18nService.getMessage('projectTagTitle');
  }

  ngOnInit(): void {
    this.teamService.currentTeam
      .pipe(
        distinctUntilChanged((team) => team.id),
        takeUntil(this.unSubscribe),
      )
      .subscribe((team) => {
        if (team.isTeam && team.id) {
          this.teamId = team.id;
          this.projectId = team.projectId;
          this.loadTeamTags();
          this.loadProjectTags();
        }
      });

    this.readonly = !this.userService.isTeamControllerOrAbove();
  }

  loadTeamTags() {
    this.isWorking = true;
    this.metadataService.getTeamTagGroups(this.teamId).subscribe(
      (tagGroups) => {
        this.isWorking = false;
        this.tagGroups = tagGroups;
        this.tagGroups.forEach((l) => l.tags.sort((a, b) => a.localeCompare(b)));
      },
      (error) => this.errorHandler.handle(error),
    );
  }

  loadProjectTags() {
    this.isWorking = true;
    this.metadataService.getProjectTagGroups(this.projectId).subscribe(
      (tagGroups) => {
        this.isWorking = false;
        this.projectTagGroups = tagGroups;
        this.projectTagGroups.forEach((l) => l.tags.sort((a, b) => a.localeCompare(b)));
      },
      (error) => this.errorHandler.handle(error),
    );
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }

  onAdd() {
    const modalRef = this.modal.open(AddTagGroupComponent, { backdrop: 'static' });
    modalRef.componentInstance.title = this.i18nService.getMessage('addTagGroup');
    modalRef.componentInstance.isForAdd = true;
    modalRef.result
      .then((model) => {
        return this.metaDataService
          .saveTeamTagGroup(this.teamId, {
            name: model.name,
            tags: model.items.map((i) => i.value),
          })
          .subscribe(
            () => {
              this.toastr.successByKey('tagGroupAdded');
              this.loadTeamTags();
            },
            (error) => this.errorHandler.handle(error),
          );
      })
      .catch(() => {});
  }

  onEdit(tag) {
    const modalRef = this.modal.open(AddTagGroupComponent, { backdrop: 'static' });
    modalRef.componentInstance.title = this.i18nService.getMessage('editTagGroup');
    modalRef.componentInstance.model = this.appUtilService.clone(tag);
    modalRef.result
      .then((model) => {
        return this.metaDataService
          .updateTeamTagGroup(this.teamId, model.id, {
            name: model.name,
            tags: model.items.map((i) => i.value),
          })
          .subscribe(
            () => {
              this.toastr.successByKey('tagGroupUpdated');
              this.loadTeamTags();
            },
            (error) => this.errorHandler.handle(error),
          );
      })
      .catch(() => {});
  }

  onRemove({ id }) {
    this.confirmService
      .confirmDelete('deleteTagGroup')
      .then(() => {
        return this.metadataService.removeTeamTagGroup(this.teamId, id).subscribe(
          () => {
            this.toastr.successByKey('tagGroupRemoved');
            this.loadTeamTags();
          },
          (error) => this.errorHandler.handle(error),
        );
      })
      .catch(() => {});
  }
}
